import { useState, useEffect } from "react";
import { Copy, Loader2, Check, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface AddonCategoryData {
  name: string;
  is_required: boolean;
  min_select: number;
  max_select: number;
  sort_order: number;
  addons: {
    name: string;
    price: number;
    is_available: boolean;
    image_url: string;
    sort_order: number;
  }[];
}

interface CopyAddonsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetProductId: string;
  targetProductName: string;
  restaurantId: string;
  onCopyComplete: () => void;
  /** If provided, returns data to the form instead of saving to DB */
  onCopyToForm?: (categories: AddonCategoryData[]) => void;
}

export const CopyAddonsDialog = ({
  open, onOpenChange, targetProductId, targetProductName, restaurantId, onCopyComplete, onCopyToForm,
}: CopyAddonsDialogProps) => {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(false);
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !restaurantId) return;
    const load = async () => {
      setLoading(true);
      // Get all products that have addons
      const { data: addons } = await supabase
        .from("product_addons")
        .select("product_id")
        .eq("product_id", targetProductId)
        .limit(1);

      const { data: allProducts } = await supabase
        .from("products")
        .select("id, name, image_url")
        .eq("restaurant_id", restaurantId)
        .neq("id", targetProductId)
        .order("name");

      // Get addon counts
      const { data: addonCounts } = await supabase
        .from("product_addons")
        .select("product_id, id")
        .eq("product_id", targetProductId);

      if (allProducts) {
        // Get addon counts for all products
        const productIds = allProducts.map(p => p.id);
        const { data: allAddons } = await supabase
          .from("product_addons")
          .select("product_id")
          .in("product_id", productIds);

        const counts: Record<string, number> = {};
        (allAddons || []).forEach(a => { counts[a.product_id] = (counts[a.product_id] || 0) + 1; });

        setProducts(
          allProducts
            .filter(p => (counts[p.id] || 0) > 0)
            .map(p => ({ ...p, addonCount: counts[p.id] || 0 }))
        );
      }
      setLoading(false);
    };
    load();
  }, [open, restaurantId, targetProductId]);

  const handleCopy = async () => {
    if (!selectedSourceId) return;
    setCopying(true);
    try {
      // Get source addon categories
      const { data: sourceCats } = await supabase
        .from("addon_categories" as any)
        .select("*")
        .eq("product_id", selectedSourceId)
        .order("sort_order");

      const { data: sourceAddons } = await supabase
        .from("product_addons")
        .select("*")
        .eq("product_id", selectedSourceId)
        .order("sort_order");

      if (!sourceCats || sourceCats.length === 0) {
        toast({ title: "Produto sem adicionais", variant: "destructive" });
        setCopying(false);
        return;
      }

      // Mode: return data to form state
      if (onCopyToForm) {
        const result = (sourceCats as any[]).map((cat: any) => ({
          name: cat.name,
          is_required: cat.is_required,
          min_select: cat.min_select,
          max_select: cat.max_select,
          sort_order: cat.sort_order,
          addons: (sourceAddons || [])
            .filter((a: any) => a.addon_category_id === cat.id)
            .map((a: any) => ({
              name: a.name,
              price: a.price,
              is_available: a.is_available,
              image_url: a.image_url || "",
              sort_order: a.sort_order,
            })),
        }));
        onCopyToForm(result);
        toast({ title: "Adicionais copiados para o formulário!" });
        onOpenChange(false);
        setCopying(false);
        return;
      }

      // Mode: save directly to DB
      for (const cat of sourceCats as any[]) {
        const { data: newCat } = await supabase
          .from("addon_categories" as any)
          .insert({
            product_id: targetProductId,
            restaurant_id: restaurantId,
            name: cat.name,
            is_required: cat.is_required,
            min_select: cat.min_select,
            max_select: cat.max_select,
            sort_order: cat.sort_order,
          })
          .select("id")
          .single();

        if (!newCat) continue;
        const newCatId = (newCat as any).id;

        const catAddons = (sourceAddons || []).filter((a: any) => a.addon_category_id === cat.id);
        for (const addon of catAddons as any[]) {
          await supabase.from("product_addons").insert({
            product_id: targetProductId,
            addon_category_id: newCatId,
            name: addon.name,
            price: addon.price,
            is_available: addon.is_available,
            image_url: addon.image_url,
            sort_order: addon.sort_order,
            min_qty: addon.min_qty || 0,
            max_qty: addon.max_qty || 1,
          });
        }
      }

      toast({ title: "Adicionais copiados com sucesso!" });
      onCopyComplete();
      onOpenChange(false);
    } catch (err: any) {
      console.error("Copy addons error:", err);
      toast({ title: "Erro ao copiar", description: err.message, variant: "destructive" });
    } finally {
      setCopying(false);
    }
  };

  const filtered = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md max-h-[75vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-sm">
            <Copy className="h-4 w-4 text-primary" />
            Copiar Adicionais para "{targetProductName}"
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-muted-foreground">
          Selecione o produto de onde copiar os adicionais:
        </p>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar produto..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-background border-border rounded-xl h-9 text-sm"
          />
        </div>

        <div className="flex-1 overflow-y-auto space-y-1.5 max-h-[40vh]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Nenhum produto com adicionais encontrado
            </p>
          ) : (
            filtered.map((prod) => (
              <button
                key={prod.id}
                onClick={() => setSelectedSourceId(prod.id === selectedSourceId ? null : prod.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                  selectedSourceId === prod.id
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/30 hover:bg-secondary/30"
                }`}
              >
                {prod.image_url ? (
                  <img src={prod.image_url} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-secondary/50 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{prod.name}</p>
                  <Badge variant="secondary" className="text-[10px] h-4 px-1.5 mt-0.5">
                    {prod.addonCount} adicionais
                  </Badge>
                </div>
                {selectedSourceId === prod.id && (
                  <Check className="h-4 w-4 text-primary shrink-0" />
                )}
              </button>
            ))
          )}
        </div>

        <Button
          onClick={handleCopy}
          disabled={!selectedSourceId || copying}
          className="rounded-xl gap-2"
        >
          {copying ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Copiando...
            </>
          ) : (
            <>
              <Copy className="h-4 w-4" />
              Copiar Adicionais
            </>
          )}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
